import {
  coupleInitials,
  coupleName,
  eventRange,
  eventShort,
  MONTHS_UPPER,
  phoneBR,
  relativeWhen,
} from '@/components/enc/format';
import {
  DashedNote,
  InitialsAvatar,
  Label,
  PageHeader,
  Panel,
  PanelTitle,
  SacramentTags,
} from '@/components/enc/primitives';
import { RETREAT_PLACE } from '@/components/enc/retreat';
import { Button } from '@/components/ui/button';
import { ContentLoader } from '@/components/ui/loaders';
import { date } from '@/lib/date';
import { useDashboard } from '@/services/dashboard';
import { useTheme } from '@/providers/theme/use-theme';
import {
  Calendar,
  ChevronRight,
  Info,
  MapPin,
  ShieldCheck,
  TrendingUp,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

function Kpi({
  icon,
  label,
  value,
  sub,
  delta,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sub: string;
  delta?: string;
}) {
  return (
    <div className="flex flex-col gap-3.5 rounded-[18px] border border-line bg-surface p-[22px]">
      <div className="flex items-center justify-between">
        <span className="flex size-[38px] items-center justify-center rounded-[10px] bg-brand-tint text-brand [&_svg]:size-[19px]">
          {icon}
        </span>
        {delta && (
          <span className="rounded-full bg-ok-tint px-[9px] py-1 text-[11px] font-bold text-ok">
            {delta}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <Label>{label}</Label>
        <span className="font-display text-[36px] leading-[1.05] font-extrabold tracking-[-0.03em] text-ink">
          {value}
        </span>
        <span className="text-[13px] text-muted">{sub}</span>
      </div>
    </div>
  );
}

interface BarPart {
  label: string;
  n: number;
  color: string;
}

function StackedBar({
  label,
  headline,
  parts,
  total,
}: {
  label: string;
  headline: string;
  parts: BarPart[];
  total: number;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-ink">{label}</span>
        <span className="font-display text-[15px] font-bold text-ink">{headline}</span>
      </div>
      <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-bar">
        {parts.map((p) => (
          <div
            key={p.label}
            title={`${p.label}: ${p.n}`}
            className="h-full"
            style={{ width: `${total ? (p.n / total) * 100 : 0}%`, background: p.color }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-3.5">
        {parts.map((p) => (
          <span key={p.label} className="flex items-center gap-1.5 text-xs text-muted">
            <span className="size-2 rounded-[2px]" style={{ background: p.color }} />
            {p.label} <strong className="font-semibold text-body">{p.n}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

const Page = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { data, isLoading } = useDashboard();
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/inscricao`);
    } catch {
      // sem permissão de área de transferência: só sinaliza
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const event = data?.event;
  const total = data?.total ?? 0;
  const goal = data?.goal ?? 80;
  const pct = (n: number) => `${total ? Math.round((n / total) * 100) : 0}%`;
  const prev = data?.previousEdition;
  const vsPrev = prev?.count ? Math.round((total / prev.count - 1) * 100) : 0;

  const C1 = 'var(--brand)';
  const C2 = theme === 'dark' ? 'rgba(239,90,95,.45)' : 'rgba(163,38,43,.35)';
  const C3 = 'var(--line-2)';

  const sacramentParts = (t: { BOTH: number; ONLY_ONE: number; NONE: number }) => [
    { label: 'Ambos', n: t.BOTH, color: C1 },
    { label: 'Somente um', n: t.ONLY_ONE, color: C2 },
    { label: 'Nenhum', n: t.NONE, color: C3 },
  ];

  const parishMax = Math.max(1, ...(data?.parishes ?? []).map((p) => p.count));

  const eyebrow = event
    ? `${eventShort(event.description).toUpperCase()} • ${
        date(event.endDate).isBefore(date()) ? 'ENCERRADO' : 'INSCRIÇÕES ABERTAS'
      }`
    : 'ENCHEI-VOS';

  return (
    <div className="flex flex-col gap-5">
      <ContentLoader show={isLoading} />

      <div className="pb-2">
        <PageHeader
          eyebrow={eyebrow}
          title="Painel de Controle"
          description="Acompanhe as inscrições e o perfil dos casais do próximo retiro."
          actions={
            <>
              <Button variant="outline" onClick={copyLink}>
                {copied ? 'Link copiado' : 'Copiar link de inscrição'}
              </Button>
              <Button onClick={() => navigate('/admin/couples/add')}>Novo casal</Button>
            </>
          }
        />
      </div>

      {!isLoading && !event && (
        <Panel className="p-6 text-sm text-muted">
          Nenhum encontro cadastrado.{' '}
          <Link to="/admin/events/add" className="font-semibold text-brand">
            Cadastrar encontro
          </Link>
        </Panel>
      )}

      {data && event && (
        <>
          {/* Linha 1 */}
          <div className="flex flex-wrap gap-4">
            <Panel className="flex flex-[1_1_420px] flex-col overflow-hidden">
              <div className="relative h-[150px] bg-[#1a1a1a]">
                <img
                  src="/assets/about-2.jpg"
                  alt="Casais no Retiro Enchei-vos"
                  className="block h-full w-full object-cover opacity-85"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_30%,rgba(0,0,0,.65))]" />
                <div className="absolute right-[22px] bottom-4 left-[22px] flex items-end justify-between gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold tracking-[.12em] text-white/80">
                      PRÓXIMO ENCONTRO
                    </span>
                    <span className="font-display text-[22px] font-extrabold tracking-[-0.02em] text-white">
                      {event.description}
                    </span>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold whitespace-nowrap text-[#0B0B0C]">
                    Faltam {data.daysLeft} dias
                  </span>
                </div>
              </div>
              <div className="flex flex-1 flex-col gap-5 p-[22px]">
                <div className="flex flex-wrap gap-5">
                  <div className="flex items-center gap-2.5 text-brand">
                    <Calendar className="size-[17px]" />
                    <span className="text-[13px] font-semibold text-body">
                      {eventRange(event)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 text-brand">
                    <MapPin className="size-[17px]" />
                    <span className="text-[13px] font-semibold text-body">
                      {RETREAT_PLACE}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-baseline justify-between">
                    <Label>META DE CASAIS</Label>
                    <span className="text-[13px] text-muted">
                      <strong className="font-display text-lg text-ink">{total}</strong> /{' '}
                      {goal}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-bar">
                    <div
                      className="h-full rounded-full bg-brand"
                      style={{ width: `${Math.min(100, (total / goal) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted">
                    {total >= goal
                      ? 'Meta da edição atingida.'
                      : `${goal - total} casais para bater a meta da edição.`}
                  </span>
                </div>
                <div className="mt-auto grid grid-cols-3 gap-2">
                  {data.editions.map((ed) => (
                    <div
                      key={ed.year}
                      className="flex flex-col gap-0.5 rounded-[12px] bg-surface-2 p-3"
                    >
                      <span className="text-[11px] font-semibold text-muted">
                        {ed.year}
                      </span>
                      <span className="font-display text-xl font-extrabold text-ink">
                        {ed.count}
                      </span>
                      <span className="text-[11px] text-muted">casais</span>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>

            <div className="grid min-w-0 flex-[1.4_1_460px] gap-4 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
              <Kpi
                icon={<Users />}
                label="CASAIS INSCRITOS"
                value={String(total)}
                sub={`${total * 2} participantes`}
                delta={data.last7 ? `+${data.last7} na semana` : undefined}
              />
              <Kpi
                icon={<TrendingUp />}
                label="VS. EDIÇÃO ANTERIOR"
                value={`${vsPrev >= 0 ? '+' : ''}${vsPrev}%`}
                sub={
                  prev ? `${prev.count} casais em ${prev.year}` : 'Sem edição anterior'
                }
              />
              <Kpi
                icon={<ShieldCheck />}
                label="CASADOS NA IGREJA"
                value={pct(data.married)}
                sub={`${data.married} de ${total} casais`}
              />
              <Kpi
                icon={<UserPlus />}
                label="COM FILHOS"
                value={pct(data.withChildren)}
                sub={`${data.withChildren} casais com filhos`}
              />
            </div>
          </div>

          {/* Linha 2 */}
          <div className="flex flex-wrap gap-4">
            <Panel className="flex flex-[2_1_560px] flex-col overflow-hidden">
              <div className="flex flex-wrap items-start justify-between gap-4 px-6 pt-6 pb-4">
                <div className="max-w-[520px]">
                  <PanelTitle
                    title="Bodas dos próximos 30 dias"
                    description="Casais inscritos que celebram aniversário de casamento. Uma bela ocasião para um gesto especial durante o retiro."
                  />
                </div>
                <span className="flex items-center gap-2 rounded-full bg-brand-tint px-3 py-1.5 text-xs font-bold text-brand">
                  {data.anniversaries.length} casais
                </span>
              </div>
              {data.anniversaries.map((a) => {
                const [, month, day] = a.date.split('-').map(Number) as [number, number, number];
                return (
                  <Link
                    key={a.coupleId}
                    to={`/admin/couples/${a.coupleId}/edit`}
                    className="flex items-center gap-4 border-t border-line px-6 py-3.5 transition-colors hover:bg-surface-2"
                  >
                    <div className="flex w-[52px] flex-none flex-col items-center overflow-hidden rounded-[12px] border border-line-2">
                      <span className="w-full bg-brand py-[3px] text-center text-[9px] font-bold tracking-[.1em] text-white">
                        {MONTHS_UPPER[month - 1]}
                      </span>
                      <span className="pt-1 pb-[5px] font-display text-xl font-extrabold text-ink">
                        {String(day).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                      <span className="truncate text-sm font-semibold text-ink">
                        {a.name}
                      </span>
                      <span className="text-[13px] text-muted">
                        {a.years} {a.years === 1 ? 'ano de casados' : 'anos de casados'}
                        {a.bodas && (
                          <>
                            {' • '}
                            <strong className="font-semibold text-body">
                              Bodas de {a.bodas}
                            </strong>
                          </>
                        )}
                      </span>
                    </div>
                    {a.onRetreat && (
                      <span className="rounded-full bg-brand px-2.5 py-1 text-[11px] font-bold whitespace-nowrap text-white max-sm:hidden">
                        No fim de semana do retiro
                      </span>
                    )}
                    <span className="min-w-16 text-right text-xs font-semibold whitespace-nowrap text-muted">
                      {a.inDays === 0
                        ? 'Hoje'
                        : a.inDays === 1
                          ? 'Amanhã'
                          : `em ${a.inDays} dias`}
                    </span>
                  </Link>
                );
              })}
              {data.anniversaries.length === 0 && (
                <div className="border-t border-line p-6 text-sm text-muted">
                  Nenhum aniversário de casamento nos próximos 30 dias.
                </div>
              )}
            </Panel>

            <Panel className="flex flex-[1_1_320px] flex-col gap-[22px] p-6">
              <PanelTitle
                title="Vida sacramental"
                description="Situação dos casais inscritos."
              />
              <StackedBar
                label="Batismo"
                headline={`${pct(data.baptism.BOTH)} ambos`}
                parts={sacramentParts(data.baptism)}
                total={total}
              />
              <StackedBar
                label="Crisma"
                headline={`${pct(data.confirmation.BOTH)} ambos`}
                parts={sacramentParts(data.confirmation)}
                total={total}
              />
              <StackedBar
                label="Matrimônio na Igreja"
                headline={pct(data.married)}
                parts={[
                  { label: 'Sim', n: data.married, color: C1 },
                  { label: 'Ainda não', n: total - data.married, color: C3 },
                ]}
                total={total}
              />
              <DashedNote icon={<Info />} className="mt-auto">
                <strong className="text-ink">{total - data.married} casais</strong> ainda
                não são casados na Igreja. Uma boa oportunidade para apresentar a
                preparação ao matrimônio.
              </DashedNote>
            </Panel>
          </div>

          {/* Linha 3 */}
          <div className="flex flex-wrap gap-4">
            <Panel className="flex flex-[2_1_560px] flex-col overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-6 pt-[22px] pb-3.5">
                <PanelTitle
                  title="Inscrições recentes"
                  description="Últimos casais que se inscreveram pelo site."
                />
                <Link
                  to="/admin/couples"
                  className="flex items-center gap-1 text-[13px] font-semibold whitespace-nowrap text-brand"
                >
                  Ver todos
                  <ChevronRight className="size-4" />
                </Link>
              </div>
              <div className="overflow-x-auto">
                <div className="min-w-[640px]">
                  <div className="grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1.2fr)_minmax(0,1fr)] gap-4 border-y border-line bg-surface-2 px-6 py-2.5 text-[10px] font-bold tracking-[.1em] text-muted">
                    <span>CASAL</span>
                    <span>PARÓQUIA</span>
                    <span>SACRAMENTOS</span>
                    <span>INSCRIÇÃO</span>
                  </div>
                  {data.recent.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => navigate(`/admin/couples/${c.id}/edit`)}
                      className="grid cursor-pointer grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1.2fr)_minmax(0,1fr)] items-center gap-4 border-b border-line px-6 py-3.5 transition-colors hover:bg-surface-2"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <InitialsAvatar text={coupleInitials(c)} />
                        <span className="flex min-w-0 flex-col gap-0.5">
                          <span className="truncate text-sm font-semibold text-ink">
                            {coupleName(c)}
                          </span>
                          <span className="text-xs text-muted">
                            {phoneBR(c.husbandPhone)}
                          </span>
                        </span>
                      </div>
                      <span className="truncate text-[13px] text-body">
                        {c.parish || 'Não informada'}
                      </span>
                      <SacramentTags couple={c} />
                      <span className="text-[13px] text-muted">
                        {relativeWhen(c.createdAt)}
                      </span>
                    </div>
                  ))}
                  {data.recent.length === 0 && (
                    <div className="px-6 py-8 text-sm text-muted">
                      Nenhuma inscrição ainda.
                    </div>
                  )}
                </div>
              </div>
            </Panel>

            <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-4">
              <Panel className="flex flex-col gap-4 p-6">
                <PanelTitle
                  title="De onde vêm os casais"
                  description="Paróquias com mais inscritos."
                />
                {data.parishes.map((p) => (
                  <div key={p.name} className="flex flex-col gap-1.5">
                    <div className="flex justify-between gap-3 text-[13px]">
                      <span className="truncate text-body">{p.name}</span>
                      <span className="font-bold text-ink">{p.count}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-bar">
                      <div
                        className="h-full rounded-full bg-brand"
                        style={{ width: `${(p.count / parishMax) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </Panel>
              <Panel className="flex flex-col gap-3.5 p-6">
                <span className="font-display text-lg font-bold text-ink">
                  Pastorais e movimentos
                </span>
                <div className="flex flex-wrap gap-2">
                  {data.movements.map((m) => (
                    <span
                      key={m.name}
                      className="flex items-center gap-2 rounded-full border border-line-2 px-3 py-2 text-[13px] text-body"
                    >
                      {m.name}
                      <strong className="font-display text-ink">{m.count}</strong>
                    </span>
                  ))}
                  {data.movements.length === 0 && (
                    <span className="text-[13px] text-muted">
                      Nenhum casal inscrito ainda.
                    </span>
                  )}
                </div>
              </Panel>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Page;
